import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDatepicker } from '@angular/material/datepicker';
import { fromEvent, Subject, takeUntil } from 'rxjs';
import { buildSingleDateObject } from '../../helpers/buildDateObject';
import { Ui2DatePickerModel } from '../../models/date-picker.model';

const CALENDAR_POPUP_SELECTOR = 'mat-datepicker-popup';

@Component({
  selector: 'ui2-datepicker-single',
  templateUrl: './ui2-datepicker-single.component.html',
  styleUrls: ['./ui2-datepicker-single.component.scss', '../../styles/date-picker-trigger.scss']
})
export class Ui2DatepickerSingleComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  @Input()
  set datePickerModel(datePickerModel: Ui2DatePickerModel) {
    this.selectedDate = datePickerModel?.beginJsDate || datePickerModel?.jsdate;
  }

  @Input()
  public placeholder: string;

  @Input()
  public clearable: boolean;

  @Output()
  dateChanged: EventEmitter<Ui2DatePickerModel> = new EventEmitter<Ui2DatePickerModel>();

  @Output()
  public focusChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('picker') picker: MatDatepicker<string>;

  public selectedDate: Date;
  public isOpen: boolean;

  private unsubscribeClickEvent: Subject<void> = new Subject();

  constructor(private readonly datePipe: DatePipe) {}

  ngAfterViewInit(): void {
    if (this.picker) {
      this.picker.closedStream.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        // on close the picker we do not want to keep listening
        // to clicks in the calendar popup
        this.unsubscribeClickEvent.next();
        this.unsubscribeClickEvent.complete();
      });
    }
  }

  public open() {
    this.focusChange.emit(true);
    this.picker.open();
    this.isOpen = true;

    this.preventClickPropagation();
  }

  public dateUpdated() {
    this.dateChanged.emit(buildSingleDateObject(this.selectedDate, this.datePipe));
  }

  public clear() {
    this.picker.close();

    this.selectedDate = null;

    this.dateChanged.emit(null);
  }

  /**
   * We want to prevent the popup calendar to populate the click events
   * to the elements behind it in order to avoid weird behaviour
   * and collisions with other implementations
   */
  private preventClickPropagation(): void {
    const domElement: HTMLCollectionOf<Element> = document.getElementsByClassName(CALENDAR_POPUP_SELECTOR);

    if (domElement?.length) {
      fromEvent(domElement[0], 'click')
        .pipe(takeUntil(this.unsubscribeClickEvent))
        .subscribe((event) => {
          event.stopPropagation();
        });
    }
  }
}
